import React, { useState, useEffect, useMemo } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { CircularProgress, useTheme } from '@mui/material';
import { darken, lighten } from '@mui/material/styles';
import { ApiCitation } from '../messages/ChatMessage';
import ClientSidePdfViewer from './PdfViewer';
import { CitationTooltipContent } from '../messages/Citations';
import { Tooltip } from '@mui/material';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';

export interface DocumentTabProps {
  id: string; // Unique identifier for the document (e.g., S3 URL or a generated ID)
  title: string; // Title for the tab (e.g., "Source 1 p.37")
  pdfUrl: string; // The actual URL to the PDF (pre-signed, configured for inline display)
  pageNumber?: number; // Optional: initial page to display or reference
  shortTitle?: string; // For the tab display
  index?: number; // For the tab display
  type_document?: string;
  messageId: string;
}

interface DocumentViewerPanelProps {
  openDocuments: DocumentTabProps[]; // Array of documents to be shown as tabs
  activeDocumentId: string | null; // ID of the currently active document
  onTabChange: (newActiveDocumentId: string, messageIdOfNewActiveDocument: string) => void; // Simplified
  onTabClose: (documentIdToClose: string) => void; // Callback to close a tab
  onCloseViewer?: () => void; // Callback to close the entire viewer panel
  isLoading?: boolean; // Optional: if parent wants to indicate loading state
  sx?: object; // For custom styling of the root Box
  className?: string;
  currentCitation: ApiCitation | null;
}

// Tab Label Component
const TabLabelContent = ({ mainText, pageText, isActive }: { mainText: string; pageText?: string; isActive: boolean }) => {
  const theme = useTheme();
  return (
    <Box sx={{ display: 'flex', alignItems: 'baseline', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
      <Typography
        component="span"
        sx={{
          fontWeight: 600,
          fontSize: '13px', // As per image
          color: isActive ? 'white' : theme.palette.text.primary,
          mr: pageText ? 0.5 : 0,
        }}
      >
        {mainText}
      </Typography>
      {pageText && (
        <Typography
          component="span"
          sx={{
            fontSize: '11px', // Smaller for "p.X"
            opacity: isActive ? 0.9 : 0.7,
            color: isActive ? 'white' : theme.palette.text.secondary,
            fontStyle: 'italic', // Italicized as in image
          }}
        >
          {pageText}
        </Typography>
      )}
    </Box>
  );
};

export function DocumentViewerPanel({
  openDocuments,
  activeDocumentId,
  onTabChange,
  onTabClose,
  onCloseViewer,
  currentCitation,
  isLoading = false,
  sx,
  className,
}: DocumentViewerPanelProps) {
  const theme = useTheme();
  // State to hold the total number of pages for the currently active PDF
  const [totalPagesInActiveDoc, setTotalPagesInActiveDoc] = useState<number | null>(null);
  const [currentPageInActiveDoc, setCurrentPageInActiveDoc] = useState<number>(1);

  const tabsOuterContainerRef = React.useRef<HTMLDivElement | null>(null);
  const [showScrollButtons, setShowScrollButtons] = React.useState({ left: false, right: false });

  const updateScrollButtonVisibility = React.useCallback(() => {
    // Get the MuiTabs-scroller element
    const scroller = tabsOuterContainerRef.current?.querySelector('.MuiTabs-scroller') as HTMLDivElement | null;
    

    if (scroller) {
      const { scrollLeft, scrollWidth, clientWidth } = scroller;

      const canScrollLeft = scrollLeft > 1;
      const canScrollRight = scrollLeft < scrollWidth - clientWidth - 1;


      if (showScrollButtons.left !== canScrollLeft || showScrollButtons.right !== canScrollRight) {
        setShowScrollButtons({
          left: canScrollLeft,
          right: canScrollRight,
        });
      }
    } else {
      if (showScrollButtons.left || showScrollButtons.right) {
        setShowScrollButtons({ left: false, right: false });
      }
    }
  }, [showScrollButtons.left, showScrollButtons.right]);

  React.useEffect(() => {
    const scroller = tabsOuterContainerRef.current?.querySelector('.MuiTabs-scroller') as HTMLDivElement | null;
    let observer: MutationObserver;

    if (scroller) {
      updateScrollButtonVisibility();
      scroller.addEventListener('scroll', updateScrollButtonVisibility);
      window.addEventListener('resize', updateScrollButtonVisibility); // Window resize might affect clientWidth

      // Observe the flex container *inside* the scroller for tab additions/removals
      const flexContainer = scroller.querySelector('.MuiTabs-flexContainer');
      if (flexContainer) {
        observer = new MutationObserver(updateScrollButtonVisibility);
        observer.observe(flexContainer, { childList: true }); // Observe direct children (tabs)
      }

      return () => {
        scroller.removeEventListener('scroll', updateScrollButtonVisibility);
        window.removeEventListener('resize', updateScrollButtonVisibility);
        if (observer) {
          observer.disconnect();
        }
      };
    }
  }, [openDocuments, updateScrollButtonVisibility]); // Re-run when openDocuments changes to re-attach listeners if scroller appears

  const activeTabData = useMemo(() => {
    return openDocuments.find(doc => doc.id === activeDocumentId);
  }, [openDocuments, activeDocumentId]);

  useEffect(() => {
    // When the active tab changes, reset total pages and set current page from tab data
    if (activeTabData) {
      setTotalPagesInActiveDoc(null); // Will be updated by ClientSidePdfViewer's onDocumentLoadSuccess
      setCurrentPageInActiveDoc(activeTabData.pageNumber || 1);
    }
  }, [activeTabData]);

  const handleInternalTabChange = (event: React.SyntheticEvent, newActiveId: string) => {
    if (newActiveId !== activeDocumentId) {
      const newlySelectedTabData = openDocuments.find(doc => doc.id === newActiveId);
      if (newlySelectedTabData) {
        onTabChange(newActiveId, newlySelectedTabData.messageId);
      }
    }
  };

  const handleInternalTabClose = (event: React.MouseEvent, tabIdToClose: string) => {
    event.stopPropagation();
    onTabClose(tabIdToClose);
  };
  
  const handlePdfPageChange = (pageNumber: number) => {
    setCurrentPageInActiveDoc(pageNumber);
  };

  const handlePdfDocumentLoadSuccess = (numPages: number) => {
    setTotalPagesInActiveDoc(numPages);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', ...sx }} className={className}>
        <CircularProgress />
      </Box>
    );
  }

  if (openDocuments.length === 0 || !activeTabData) {
    return (
      <Box sx={{ padding: 2, textAlign: 'center', mt: 4, height: '100%', ...sx }} className={className}>
        <Typography variant="h6" color="text.secondary">
          Select a source
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Click a source in the chat to view the document here.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      className={className}
      sx={{
        display: 'flex',
        padding: '0px !important',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
        background: '#f6f6f6 !important',
        borderRadius: '0px 20px 20px 0px',
        bgcolor: theme.palette.background.default,
        borderLeft: `1px solid ${theme.palette.divider}`,
        ...sx,
      }}
    >
      {/* Top Bar: Tabs and Close Panel Button */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: '#f6f6f6',
          padding: theme.spacing(0, 1, 0, 0.5),
          borderRadius: '0px 20px 0px 0px',
          flexShrink: 0,
          position: 'relative',
        }}
      >
        {showScrollButtons.left && (
          <>
            <Box sx={{ position: 'relative', bgcolor: "#f6f6f6", height: '100%', minWidth: '48px', width: '10%' }}>
                <IconButton
                    size="small"
                    onClick={() => {
                        const scroller = tabsOuterContainerRef.current?.querySelector('.MuiTabs-scroller') as HTMLDivElement | null;
                        if (scroller) {
                            const direction = -1;
                            let proportion = 0.7;
                            if (window.matchMedia("(max-width: 768px)").matches) {
                                proportion = 0.8; 
                            }
                            const scrollAmount = scroller.clientWidth * proportion;
                            scroller.scrollBy({ left: direction * scrollAmount, behavior: 'smooth' });
                        }
                    }}
                    sx={{
                        color: 'gray',
                        width: 'auto',
                        border: "1px solid #c9c9d1",
                        borderRadius: '0px !important',
                        borderTop: 'none',
                        borderBottom: 'none',
                        height: '28px',
                        minWidth: '28px',
                        left: '0px',
                        marginLeft: '5px',
                        marginTop: '10px',
                        '&:hover': {
                            background: theme.palette.action.hover,
                        }
                    }}
                    title="Défiler à gauche"
                    >
                    <MoreHorizIcon sx={{ fontSize: '25px' }} />
                </IconButton>
            </Box>
          </>
        )}
        <Box
          ref={tabsOuterContainerRef}
          sx={{
            flexGrow: 1,
            overflowX: 'hidden',
            display: 'flex',
          }}
        >
          <Tabs
            value={activeDocumentId}
            onChange={handleInternalTabChange}
            variant="scrollable"
            scrollButtons={false}
            aria-label="document viewer tabs"
            sx={{
              minHeight: 'auto',
              '& .MuiTabs-indicator': { display: 'none' },
              '& .MuiTabs-flexContainer': { gap: theme.spacing(0.75) },
              padding: "0px 0px 0px 1px",
              borderRadius: '0px',
              width: '100%',
              '& .MuiTabs-scroller': {
                overflowX: 'auto !important',
                '&::-webkit-scrollbar': { display: 'none' },
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
              },
            }}
          >
            {openDocuments.map((doc) => {
              const isActive = doc.id === activeDocumentId;
              return (
                <Tab
                  key={doc.id}
                  value={doc.id}
                  disableRipple
                  label={
                    <Tooltip
                      key={`cit-marker-${doc.index}`}
                      componentsProps={{
                        tooltip: { // Styles the .MuiTooltip-tooltip class (the main body)
                        sx: {
                            backgroundColor: 'transparent',
                            boxShadow: 'none'// Allow CitationTooltipContent to set its own maxWidth
                        },
                        },
                      }}
                      title={
                        <CitationTooltipContent
                          citationNumber={doc.index! + 1}
                          documentName={doc.title || 'Document ' + (doc.index! + 1)}
                          pageNumber={doc.pageNumber || 1}
                          documentType={doc.type_document}
                          documentUrl={doc.pdfUrl}
                        />}
                        placement="top"
                        >
        
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                      <TabLabelContent
                        mainText={`Source ${doc.index! + 1}`}
                        pageText={doc.pageNumber ? `p.${doc.pageNumber}` : undefined}
                        isActive={isActive}
                        />
                    </Box>
                    </Tooltip>
                  }
                  title={doc.title}
                  sx={{
                    minHeight: '28px', height: '28px',
                    padding: `0 6px !important`,
                    borderRadius: '14px !important',
                    borderBottomLeftRadius: '0px !important',
                    borderBottomRightRadius: '0px !important',
                    border: `1px solid ${isActive ? 'transparent' : '#2563eb'}`,
                    borderBottom: 'none',
                    background: isActive ? 'linear-gradient(91deg, #3660F4 0%, #3660F4 100%)' : theme.palette.background.paper,
                    color: isActive ? 'white' : theme.palette.text.primary,
                    marginTop: '5px',
                    boxShadow: isActive ? '2px -2px 4px rgba(0, 0, 0, 0.5)' : '2px -2px 4px rgba(0, 0, 0, 0.1)',
                    transition: 'all 0.2s ease-in-out',
                    '&.Mui-selected': { color: 'white' },
                    '&:hover': {
                      background: isActive ? undefined : 'linear-gradient(98.56deg, #dbe7ff, #e9d8ff)',
                      borderColor: isActive ? 'transparent' : '#2563eb',
                      opacity: 1,
                    },
                    borderBottomWidth: '1px !important', borderTopWidth: '1px !important',
                    borderLeftWidth: '1px !important', borderRightWidth: '1px !important',
                    minWidth: 'auto',
                  }}
                />
              );
            })}
          </Tabs>
        </Box>
        {showScrollButtons.right && (
            <>
                <Box sx={{ position: 'relative', bgcolor: "#f6f6f6", width: '10%', height: '100%' }}>
                    <IconButton
                        size="small"
                        onClick={() => {
                            const scroller = tabsOuterContainerRef.current?.querySelector('.MuiTabs-scroller') as HTMLDivElement | null;
                            if (scroller) {
                                const direction = 1;
                                let proportion = 0.7;
                                if (window.matchMedia("(max-width: 768px)").matches) {
                                    proportion = 0.8;
                                }
                                const scrollAmount = scroller.clientWidth * proportion;
                                scroller.scrollBy({ left: direction * scrollAmount, behavior: 'smooth' });
                            }
                        }}
                        sx={{
                            color: 'gray',
                            width: 'auto',
                            border: "1px solid #c9c9d1",
                            borderRadius: '0px !important',
                            borderTop: 'none',
                            borderBottom: 'none',
                            height: '28px',
                            minWidth: '28px',
                            right: '-10px',
                            marginRight: '4rem',
                            marginTop: '10px',
                            '&:hover': {
                                background: theme.palette.action.hover,
                            }
                        }}
                        title="Défiler à droite"
                        >
                        <MoreHorizIcon sx={{ fontSize: '25px' }} />
                    </IconButton>
                </Box>
            </>
        )}
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0, paddingLeft: 1 }}>
            {onCloseViewer && (
                <IconButton 
                  onClick={onCloseViewer} 
                  size="small" 
                  title="Fermer le panneau" 
                  sx={{ 
                    color: theme.palette.text.secondary,
                    width: '28px',
                    height: '28px',
                    marginLeft: theme.spacing(0.5),
                    '&:hover': {
                      backgroundColor: theme.palette.action.hover,
                    }
                  }}
                >
                <img 
                    src="/assets/Vector.png"
                    alt="Close panel" 
                    style={{ 
                      width: '100%',
                      height: '100%',
                      display: 'block'
                    }} 
                    />
                </IconButton>
            )}
        </Box>
      </Box>

      {/* PDF Viewer Area */
      
      }
      <Box sx={{
        flexGrow: 1,
        overflow: 'hidden',
        position: 'relative',
        padding: "0px 5px !important",
        borderRadius: '0px 0px 20px 20px',
        marginTop: '-3px',
        boxShadow: '0px -4px 6px rgba(0, 0, 0, 0.08)',
      }}>
        {activeTabData && (
          <ClientSidePdfViewer
            key={activeTabData.id}
            title={activeTabData.title === "" || activeTabData.title == null ? "Document " + (activeTabData.index! + 1) : activeTabData.title}
            pdfUrl={activeTabData.pdfUrl}
            initialPageNumber={activeTabData.pageNumber || 1}
            onPageChange={handlePdfPageChange}
            onDocumentLoadSuccess={handlePdfDocumentLoadSuccess}
          />
        )}
        {!activeTabData && openDocuments.length > 0 && (
             <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <Typography color="textSecondary">Please select a tab to view.</Typography>
            </Box>
        )}
      </Box>
    </Box>
  );
}